/**
 * `SitePlansCard` — ADR-0263 §2.2 staff surface for the per-site plan
 * assignment on an account.
 *
 * GET `/internal/admin/accounts/:id/site-plans` on mount; the response
 * carries each site's assigned plan, resolved plan (with the catalog
 * default fallback), and any pending end-of-cycle downgrade.
 *
 * Admin role gets a plan select per row (site-scope plans only,
 * non-archived). "Use default" clears the explicit assignment, so the
 * site falls back to the active default site plan — or to "no plan"
 * (unlimited) when nothing resolves. Changing a plan opens a confirm
 * dialog: staff writes apply now and replace any scheduled change.
 *
 * Support role sees the table read-only.
 *
 * INERT in S1: nothing on the merchant side reads `site_plan` for
 * enforcement. The InertBanner (v2) at the top of the catalog surface
 * carries the global notice.
 */

import { useEffect, useMemo, useState } from 'react';

import type {
  AdminAccountSitePlanRow,
  AdminPlanDetail,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useAuth } from '../../app/auth-context.js';
import { Card } from '../../components/atoms/Card.js';
import { ConfirmDialog } from '../../components/atoms/ConfirmDialog.js';
import { Pill } from '../../components/atoms/Pill.js';
import { useC } from '../../lib/theme.js';
import { formatIsoCompact } from '../../lib/format.js';
import { hasRole } from '../../lib/roles.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface SitePlansCardProps {
  accountId: string;
}

export function SitePlansCard({ accountId }: SitePlansCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const auth = useAuth();
  const staffRole = auth.state.kind === 'authenticated'
    ? auth.state.staff.role
    : undefined;
  const canEdit = hasRole(staffRole, 'admin');

  const [rows, setRows] = useState<readonly AdminAccountSitePlanRow[]>([]);
  const [defaultPlanId, setDefaultPlanId] = useState<string | null>(null);
  const [plans, setPlans] = useState<readonly AdminPlanDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pending, setPending] = useState<null | {
    row: AdminAccountSitePlanRow;
    planId: string | null;
  }>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    try {
      const [sp, pl] = await Promise.all([
        api.getAccountSitePlans(accountId),
        api.getPlans(),
      ]);
      setRows(sp.sites);
      setDefaultPlanId(sp.default_site_plan_id);
      setPlans(pl.plans);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'unauthorized') return;
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? ` (${err.server_reason})`
          : '';
      setError(`Could not load site plans${reason}.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async (): Promise<void> => {
      if (cancelled) return;
      await refresh();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, accountId]);

  // Index plans by id for the "pending plan" lookup in the table.
  const planById = useMemo(() => {
    const m = new Map<string, AdminPlanDetail>();
    for (const p of plans) m.set(p.plan.plan_id, p);
    return m;
  }, [plans]);

  const sitePlanOptions = useMemo(
    () =>
      plans.filter(
        (p) => p.plan.scope === 'site' && p.plan.status !== 'archived',
      ),
    [plans],
  );

  const defaultName = (): string => {
    if (defaultPlanId === null) return 'No plan · unlimited';
    const p = planById.get(defaultPlanId);
    return p === undefined ? 'Default' : `Default · ${p.plan.name}`;
  };

  const resolvedLabel = (r: AdminAccountSitePlanRow): string => {
    if (r.assigned_plan_id !== null) {
      const p = planById.get(r.assigned_plan_id);
      return p?.plan.name ?? r.assigned_plan_id;
    }
    if (defaultPlanId === null) return 'No plan · unlimited';
    const p = planById.get(defaultPlanId);
    return `Default · ${p?.plan.name ?? 'Free'}`;
  };

  const onConfirmChange = async (): Promise<void> => {
    if (pending === null) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.putSitePlan(accountId, pending.row.site_id, {
        plan_id: pending.planId,
      });
      await refresh();
      setPending(null);
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setSaveError(`Could not change plan${reason}.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={{ padding: 20 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        Site plans
      </div>
      <h2
        style={{
          fontFamily: serifFont,
          fontSize: 22,
          color: C.text,
          margin: 0,
          marginBottom: 10,
        }}
      >
        Per-site plan
      </h2>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          marginBottom: 14,
          lineHeight: 1.5,
        }}
      >
        Each site resolves to its assigned plan, or the catalog default
        ({defaultName()}). Staff changes apply now and replace any
        scheduled change.
      </div>

      {loading ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 1.5,
          }}
        >
          Loading…
        </div>
      ) : error !== null ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.coralFg,
          }}
        >
          {error}
        </div>
      ) : rows.length === 0 ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
          }}
        >
          This account has no sites.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              borderCollapse: 'collapse',
              fontFamily: sansFont,
              width: '100%',
              minWidth: 560,
            }}
          >
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                <Th>Site</Th>
                <Th>Current plan</Th>
                <Th>Pending change</Th>
                {canEdit ? <Th>Assign</Th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <SitePlanRow
                  key={r.site_id}
                  row={r}
                  isLast={idx === rows.length - 1}
                  resolvedLabel={resolvedLabel(r)}
                  pendingLabel={pendingLabel(r, planById)}
                  canEdit={canEdit}
                  options={sitePlanOptions}
                  onPick={(planId) => setPending({ row: r, planId })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={pending !== null}
        title={
          pending === null
            ? ''
            : pending.planId === null
              ? 'Clear this site’s plan assignment?'
              : 'Change this site’s plan?'
        }
        body={
          pending === null
            ? ''
            : `Staff changes apply now and replace any scheduled change. ${
                pending.planId === null
                  ? 'The site will fall back to the catalog default.'
                  : `The site will move to ${
                      planById.get(pending.planId)?.plan.name ?? 'the chosen plan'
                    } now.`
              }`
        }
        confirmLabel={pending?.planId === null ? 'Clear assignment' : 'Change plan'}
        tone="lime"
        pending={saving}
        onConfirm={() => void onConfirmChange()}
        onCancel={() => {
          if (saving) return;
          setPending(null);
          setSaveError(null);
        }}
      />
      {saveError !== null ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.coralFg,
            marginTop: 8,
          }}
        >
          {saveError}
        </div>
      ) : null}
    </Card>
  );
}

function pendingLabel(
  r: AdminAccountSitePlanRow,
  planById: ReadonlyMap<string, AdminPlanDetail>,
): string | null {
  if (r.pending_plan_id === null || r.pending_effective_at === null) {
    return null;
  }
  const p = planById.get(r.pending_plan_id);
  return `${p?.plan.name ?? r.pending_plan_id} on ${formatIsoCompact(
    r.pending_effective_at,
  )}`;
}

function Th({ children }: { children: React.ReactNode }): JSX.Element {
  const C = useC();
  return (
    <th
      className="text-left px-3 py-3"
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        color: C.muted,
        letterSpacing: 2,
        textTransform: 'uppercase',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function SitePlanRow({
  row,
  isLast,
  resolvedLabel,
  pendingLabel: pending,
  canEdit,
  options,
  onPick,
}: {
  row: AdminAccountSitePlanRow;
  isLast: boolean;
  resolvedLabel: string;
  pendingLabel: string | null;
  canEdit: boolean;
  options: readonly AdminPlanDetail[];
  onPick: (planId: string | null) => void;
}): JSX.Element {
  const C = useC();
  return (
    <tr
      style={{
        borderBottom: isLast ? 'none' : `1px solid ${C.borderSoft}`,
      }}
    >
      <td className="px-3 py-3">
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            fontWeight: 500,
          }}
        >
          {row.name ?? row.domain ?? row.site_id}
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.dim,
            marginTop: 2,
          }}
        >
          {row.domain ?? row.site_id}
        </div>
      </td>
      <td
        className="px-3 py-3"
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.body,
        }}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span>{resolvedLabel}</span>
          {row.assigned_plan_id === null ? (
            <Pill tone="sky">Default</Pill>
          ) : row.source === 'staff' ? (
            <Pill tone="amber">Staff</Pill>
          ) : null}
        </div>
      </td>
      <td
        className="px-3 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: pending === null ? C.dim : C.body,
          whiteSpace: 'nowrap',
        }}
      >
        {pending ?? '—'}
      </td>
      {canEdit ? (
        <td className="px-3 py-3">
          <select
            value={row.assigned_plan_id ?? ''}
            onChange={(e) =>
              onPick(e.target.value === '' ? null : e.target.value)
            }
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '6px 8px',
              minHeight: 36,
              maxWidth: 260,
            }}
            aria-label={`Assign plan to ${row.domain ?? row.site_id}`}
          >
            <option value="">(Use default)</option>
            {options.map((p) => (
              <option key={p.plan.plan_id} value={p.plan.plan_id}>
                {p.plan.name} · {p.plan.status}
              </option>
            ))}
          </select>
        </td>
      ) : null}
    </tr>
  );
}
